import React from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { LoginProjectConfiguration } from '../../../api';
import { API_DOCUMENTATION_LINKS } from '../../../constants';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { HorizontalCenterColumnBlock } from '../../../styled';
import { FieldBlock, FieldSwitch, FormFooter, HeaderTitle } from '../../ui';
import { LinkUnderline } from '../../ui/Link';

type Props = RouteComponentProps & {
  projectId: string;
};

const ApiSettingsForm = ({ history, projectId }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { currentProject, updateCurrentProject, currentUpdateFetching } =
    useCurrentProject(projectId);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const onSubmit = async (values: LoginProjectConfiguration) => {
    try {
      await updateCurrentProject(values);
      showSuccessNotify();
    } catch (err) {
      showErrorNotify();
    }
  };

  const backToNavigation = () => history.push({ pathname: navigationPath(projectId) });

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        ...currentProject
      }}
      keepDirtyOnReinitialize>
      {({ handleSubmit }) => {
        return (
          <>
            <HorizontalCenterColumnBlock>
              <HeaderTitle
                title={intl.formatMessage({ id: 'api-settings.title' })}
                subtitle={intl.formatMessage({ id: 'api-settings.subtitle' })}
              />

              <FieldBlock>
                <FieldSwitch
                  name="is_device_auth_allowed"
                  caption={intl.formatMessage(
                    {
                      id: 'api-settings.device-id.subtitle'
                    },
                    {
                      a: (chunk: string) => (
                        <LinkUnderline
                          data-test-id="api-settings__device-id-link"
                          href={API_DOCUMENTATION_LINKS.DEVICE_ID}
                          target="_blank"
                          rel="noreferrer">
                          {chunk}
                        </LinkUnderline>
                      )
                    }
                  )}
                  label={intl.formatMessage({
                    id: 'api-settings.device-id.title'
                  })}
                />
              </FieldBlock>
            </HorizontalCenterColumnBlock>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              fetching={currentUpdateFetching}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(ApiSettingsForm);
